import { useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Loader2, ShieldCheck, Clock, XCircle } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button, Input, Separator, Checkbox } from "@akuna/ui";
import { Label } from "@/components/ui/label";
import {
  useTermSignatureView,
  useVerifyResponsibleCpf,
  useSignTerm,
  getSignedTermDownloadUrl,
} from "@/hooks/usePublicTermSignature";
import { formatCpf, stripCpf } from "@/lib/utils/cpf-mask";

type Step = "identity" | "sign" | "success";

function errorKey(message: string | undefined): string {
  const known = [
    "cpf_mismatch",
    "expired",
    "already_signed",
    "consent_required",
    "name_required",
    "not_found",
    "vet_not_signed",
  ];
  return message && known.includes(message) ? message : "generic";
}

export default function SignTerm() {
  const { token } = useParams<{ token: string }>();
  const { t } = useTranslation("terms");
  const view = useTermSignatureView(token);
  const verifyCpf = useVerifyResponsibleCpf(token ?? "");
  const signTerm = useSignTerm(token ?? "");

  const [step, setStep] = useState<Step>("identity");
  const [cpf, setCpf] = useState("");
  const [typedName, setTypedName] = useState("");
  const [consent, setConsent] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // The responsible must type their own name — do not prefill it, so the typed
  // name is a deliberate act that feeds the cursive signature.

  const handleVerifyCpf = async () => {
    setFormError(null);
    try {
      await verifyCpf.mutateAsync(stripCpf(cpf));
      setStep("sign");
    } catch (e) {
      setFormError(errorKey((e as Error).message));
    }
  };

  const handleSign = async () => {
    setFormError(null);
    try {
      const res = await signTerm.mutateAsync({ cpf: stripCpf(cpf), typedName, consent });
      if (res.ok) setStep("success");
    } catch (e) {
      setFormError(errorKey((e as Error).message));
    }
  };

  const shell = (children: React.ReactNode) => (
    <div className="min-h-screen bg-muted/30 px-4 py-8 flex items-start justify-center">
      <Card className="w-full max-w-2xl">{children}</Card>
    </div>
  );

  if (view.isLoading) {
    return shell(
      <CardContent className="flex items-center gap-3 py-12 justify-center text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
        {t("signature.page.loading")}
      </CardContent>,
    );
  }

  if (view.isError) {
    const key = errorKey((view.error as Error)?.message);
    const expired = key === "expired";
    return shell(
      <>
        <CardHeader className="items-center text-center">
          {expired ? <Clock className="h-10 w-10 text-muted-foreground" /> : <XCircle className="h-10 w-10 text-destructive" />}
          <CardTitle>{t(expired ? "signature.page.expiredTitle" : "signature.page.errorTitle")}</CardTitle>
          <CardDescription>
            {t(expired ? "signature.page.expiredBody" : "signature.page.errorBody")}
          </CardDescription>
        </CardHeader>
      </>,
    );
  }

  const data = view.data!;

  if (data.status === "expired") {
    return shell(
      <CardHeader className="items-center text-center">
        <Clock className="h-10 w-10 text-muted-foreground" />
        <CardTitle>{t("signature.page.expiredTitle")}</CardTitle>
        <CardDescription>{t("signature.page.expiredBody")}</CardDescription>
      </CardHeader>,
    );
  }

  if (data.status === "completed" || step === "success") {
    const completedNow = step === "success";
    return shell(
      <>
        <CardHeader className="items-center text-center">
          <CheckCircle2 className="h-10 w-10 text-emerald-600" />
          <CardTitle>{t(completedNow ? "signature.page.successTitle" : "signature.page.completedTitle")}</CardTitle>
          <CardDescription>
            {t(completedNow ? "signature.page.successBody" : "signature.page.completedBody")}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex justify-center pb-8">
          {token && (
            <Button asChild>
              <a href={getSignedTermDownloadUrl(token)} target="_blank" rel="noreferrer">
                {t("signature.page.downloadSigned")}
              </a>
            </Button>
          )}
        </CardContent>
      </>,
    );
  }

  // in_progress → interactive flow
  return shell(
    <>
      <CardHeader>
        <CardTitle>{data.term.title}</CardTitle>
        <CardDescription className="flex items-start gap-2">
          <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0" />
          {t("signature.page.legalNotice")}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {data.term.renderedHtml && (
          <div
            className="max-h-72 overflow-y-auto rounded-lg border bg-background p-4 text-sm prose prose-sm max-w-none"
            // Rendered term HTML comes from our own trusted backend.
            dangerouslySetInnerHTML={{ __html: data.term.renderedHtml }}
          />
        )}

        <Separator />

        {step === "identity" && (
          <div className="space-y-4">
            <div className="space-y-1">
              <h3 className="font-medium">{t("signature.identity.title")}</h3>
              <p className="text-sm text-muted-foreground">{t("signature.identity.description")}</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="sign-cpf">{t("signature.identity.cpfLabel")}</Label>
              <Input
                id="sign-cpf"
                inputMode="numeric"
                maxLength={14}
                placeholder={t("signature.identity.cpfPlaceholder")}
                value={cpf}
                onChange={(e) => setCpf(formatCpf(e.target.value))}
              />
            </div>
            {formError && <p className="text-sm text-destructive">{t(`signature.errors.${formError}`)}</p>}
            <Button onClick={handleVerifyCpf} disabled={verifyCpf.isPending || stripCpf(cpf).length !== 11}>
              {verifyCpf.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {verifyCpf.isPending ? t("signature.identity.verifying") : t("signature.identity.submit")}
            </Button>
          </div>
        )}

        {step === "sign" && (
          <div className="space-y-4">
            <h3 className="font-medium">{t("signature.sign.title")}</h3>
            <div className="space-y-2">
              <Label htmlFor="sign-name">{t("signature.sign.nameLabel")}</Label>
              <Input
                id="sign-name"
                placeholder={t("signature.sign.namePlaceholder")}
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
              />
            </div>
            {typedName.trim() && (
              <div className="rounded-lg border bg-background p-4 text-center">
                <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
                  {t("signature.sign.previewLabel")}
                </p>
                <p className="font-signature text-4xl leading-none">{typedName}</p>
              </div>
            )}
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={consent} onCheckedChange={(v) => setConsent(v === true)} className="mt-0.5" />
              <span>{t("signature.sign.consentLabel")}</span>
            </label>
            {formError && <p className="text-sm text-destructive">{t(`signature.errors.${formError}`)}</p>}
            <Button onClick={handleSign} disabled={signTerm.isPending || !consent || typedName.trim().length < 2}>
              {signTerm.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {signTerm.isPending ? t("signature.sign.signing") : t("signature.sign.submit")}
            </Button>
          </div>
        )}
      </CardContent>
    </>,
  );
}
